import { useCallback, useEffect, useMemo, useState } from 'react'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { usePollingEffect } from '../../../apps/professional-app/fa/common/hooks/usePollingEffect'
import { parseMetadata } from '../../../apps/professional-app/fa/MeetingSummary/utils'

import { useExecuteAiAgentMutation, useInteractWithAiAgentMutation } from '../../../store/ai/aiAgent'
import { useLazyGetAiAgentResultQuery } from '../../../store/ai/aiAgentResult'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'

import { EmailResponseExecutionResult, EmailResponseInteractionResult, EmailResponseResult } from '../../../models/EmailResponse'
import { AIAgentMessage, AIAgentResult, AIAgentType } from '../../../models/AIAgent'

import { Citation } from '../AIAgentResultCitations/AIAgentResultCitations'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'

interface EmailResponseProps {
  contactId: string
}

export const useEmailResponse = ({ contactId }: EmailResponseProps) => {
  const accountId = useAppSelector(selectAccountId)
  const { tracked } = useTracking()
  const { hasQuota, getQuotaValue } = useZocksQuota()

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()
  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()
  const [triggerInteractWithAiAgent] = useInteractWithAiAgentMutation()

  const [isLoading, setLoading] = useState<boolean>(true)
  const [interactionIsLoading, setInteractionIsLoading] = useState<boolean>(false)
  const [citationIsLoading, setCitationIsLoading] = useState<boolean>(false)

  const [aiAgentId, setAiAgentId] = useState<string | null>(null)
  const [existingEmailResponse, setExistingEmailResponse] = useState<AIAgentResult | null>(null)
  const [emailResponse, setEmailResponse] = useState<EmailResponseResult | null>(null)
  const [messages, setMessages] = useState<AIAgentMessage[]>([])
  const [citations, setCitations] = useState<Citation[] | null>(null)

  const autoGenerateCitations = hasQuota('ai.autoGenerateCitations.value')
  const generateCitationsForAgents = getQuotaValue('ai.generateCitationsForAgents.value')
  const citationEnabled = useMemo(
    () => autoGenerateCitations && generateCitationsForAgents.includes(AIAgentType.EMAIL_RESPONSE),
    [autoGenerateCitations, generateCitationsForAgents],
  )

  const parseAgentResult = useCallback((emailResponseResult: EmailResponseExecutionResult | EmailResponseInteractionResult) => {
    const { result } = emailResponseResult
    setEmailResponse((prevState) => ({ ...prevState, ...result }))
    const { message } = result
    if (message) {
      setMessages((messages) => [...messages, { type: 'agent', message }])
    }
  }, [])

  const parseExistingAgentResult = useCallback((aiAgentResult: AIAgentResult) => {
    const { originalAiAgentId, result, interaction, metadata } = aiAgentResult
    const { aiAgentId, interactions = [] } = interaction ?? {}
    setAiAgentId(aiAgentId ?? originalAiAgentId)
    setEmailResponse((prevState) => ({ ...prevState, ...(result as EmailResponseResult) }))
    setMessages(interactions.map((i) => i.result))
    setLoading(false)
    const { citations, startedGenerating } = parseMetadata(metadata)
    if (citations) {
      setCitations(citations)
      return
    }
    if (startedGenerating) {
      setCitationIsLoading(true)
    }
  }, [])

  const _handleEmailResponseExecution = useCallback(
    async (email: string) => {
      setLoading(true)
      setMessages([])
      setCitationIsLoading(false)
      setCitations(null)
      try {
        const emailResponseExecutionResult = (await triggerExecuteAiAgent({
          accountId,
          payload: {
            type: AIAgentType.EMAIL_RESPONSE,
            context: {
              type: 'contact',
              contactId,
              email,
            },
          },
        }).unwrap()) as EmailResponseExecutionResult
        const { aiAgentId } = emailResponseExecutionResult
        setAiAgentId(aiAgentId)
        setExistingEmailResponse(null)
        parseAgentResult(emailResponseExecutionResult)

        if (citationEnabled) {
          setCitationIsLoading(true)
        }
      } finally {
        setLoading(false)
      }
    },
    [accountId, citationEnabled, contactId, parseAgentResult, triggerExecuteAiAgent],
  )

  const handleEmailResponseExecution = tracked(
    _handleEmailResponseExecution,
    {
      eventName: TrackEvent.EMAIL_RESPONSE_EXECUTED,
      eventDetails: { contactId },
    },
    {
      eventDetailsExtractor: (email: string) => ({
        email,
      }),
    },
  )

  const _handleEmailResponseChatInteraction = useCallback(
    async (chatMessage: string) => {
      if (!aiAgentId) {
        return
      }
      setInteractionIsLoading(true)
      try {
        setMessages((messages) => [...messages, { type: 'user', message: chatMessage }])
        const emailResponseInteractionResult = (await triggerInteractWithAiAgent({
          accountId,
          aiAgentId,
          payload: { type: 'chat', query: chatMessage },
        }).unwrap()) as EmailResponseInteractionResult
        const { newAiAgentId } = emailResponseInteractionResult
        if (newAiAgentId) {
          setAiAgentId(newAiAgentId)
        }
        parseAgentResult(emailResponseInteractionResult)
      } catch (e) {
        console.error(e)
      } finally {
        setInteractionIsLoading(false)
      }
    },
    [accountId, aiAgentId, parseAgentResult, triggerInteractWithAiAgent],
  )

  const handleEmailResponseChatInteraction = tracked(
    _handleEmailResponseChatInteraction,
    {
      eventName: TrackEvent.EMAIL_RESPONSE_INTERACTED_WITH,
      eventDetails: { contactId, type: 'chat' },
    },
    {
      eventDetailsExtractor: (chatMessage: string) => ({
        chatMessage,
      }),
    },
  )

  const _handleEmailResponseEditInteraction = useCallback(
    async (editedEmail: string) => {
      if (!aiAgentId) {
        return
      }
      setInteractionIsLoading(true)
      try {
        const emailResponseInteractionResult = (await triggerInteractWithAiAgent({
          accountId,
          aiAgentId,
          payload: { type: 'edit', email: editedEmail },
        }).unwrap()) as EmailResponseInteractionResult
        const { newAiAgentId } = emailResponseInteractionResult
        if (newAiAgentId) {
          setAiAgentId(newAiAgentId)
        }
        parseAgentResult(emailResponseInteractionResult)
        return emailResponseInteractionResult
      } catch (e) {
        console.error(e)
      } finally {
        setInteractionIsLoading(false)
      }
    },
    [accountId, aiAgentId, parseAgentResult, triggerInteractWithAiAgent],
  )

  const handleEmailResponseEditInteraction = tracked(
    _handleEmailResponseEditInteraction,
    {
      eventName: TrackEvent.EMAIL_RESPONSE_INTERACTED_WITH,
      eventDetails: { contactId, type: 'edit' },
    },
    {
      eventDetailsExtractor: (editedEmail: string) => ({
        editedEmail,
      }),
    },
  )

  const getEmailResponseAgentResult = useCallback(async () => {
    try {
      return await triggerGetAiAgentResult(
        {
          accountId,
          type: AIAgentType.EMAIL_RESPONSE,
          resultKey: contactId,
        },
        false,
      ).unwrap()
    } catch (err) {
      console.error(err)
      return null
    }
  }, [accountId, contactId, triggerGetAiAgentResult])

  const getCitations = useCallback(async () => {
    const aiAgentResult = await getEmailResponseAgentResult()
    if (!aiAgentResult) {
      setCitationIsLoading(false)
      return
    }
    const { citations } = parseMetadata(aiAgentResult.metadata)
    if (!citations) {
      return
    }
    parseExistingAgentResult(aiAgentResult)
    setCitationIsLoading(false)
  }, [getEmailResponseAgentResult, parseExistingAgentResult])

  const { start: startPollingCitations, stop: stopPollingCitations } = usePollingEffect(getCitations, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (citationIsLoading) {
      startPollingCitations()
    } else {
      stopPollingCitations()
    }
  }, [citationIsLoading, startPollingCitations, stopPollingCitations])

  useEffect(() => {
    const getEmailResponse = async () => {
      const aiAgentResult = await getEmailResponseAgentResult()
      if (aiAgentResult) {
        setExistingEmailResponse(aiAgentResult)
        parseExistingAgentResult(aiAgentResult)
        return
      }
      setLoading(false)
    }
    getEmailResponse()
  }, [getEmailResponseAgentResult, parseExistingAgentResult])

  return {
    isLoading,
    interactionIsLoading,
    existingEmailResponse,
    emailResponse,
    messages,
    handleEmailResponseChatInteraction,
    handleEmailResponseEditInteraction,
    handleEmailResponseExecution,
    citations,
    citationIsLoading,
  }
}
